import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Printer, QrCode, ArrowRightLeft, Signal, CreditCard } from "lucide-react";
import { Seo } from "../components/Seo";
import { HERO_IMG } from "./Wireless";

const SITE = "https://flexlines.store";
const PLANS_URL = `${SITE}/wireless?src=poster`;
const HOME_URL = `${SITE}/?src=poster`;

const PLANS = [
    { name: "Flex 1GB", price: "19.99", data: "1 GB 5G/LTE data", note: "Seniors 62+: $14.99" },
    { name: "Flex 3GB", price: "29.99", data: "3 GB 5G/LTE + hotspot", popular: true },
    { name: "Flex 5GB", price: "39.99", data: "5 GB 5G/LTE + hotspot" },
    { name: "Pay-As-You-Go", price: "19.99", data: "+ $15/GB only when used", payg: true },
];

const STEPS = [
    { icon: QrCode, t: "Scan", d: "Pick your plan on your phone" },
    { icon: CreditCard, t: "Pay", d: "Secure checkout, no contract" },
    { icon: ArrowRightLeft, t: "Keep your number", d: "We handle the transfer" },
    { icon: Signal, t: "Connected", d: "eSIM live in minutes" },
];

export default function Poster() {
    return (
        <div className="min-h-screen bg-zinc-100 py-8 px-4 print:bg-white print:p-0" data-testid="poster-page">
            <Seo title="FlexLine Mobile — Marketing Poster | Wireless eSIM Plans from $19.99"
                description="Print-ready FlexLine Mobile poster: eSIM wireless plans from $19.99/mo with unlimited talk & text. Scan the QR to choose a plan at flexlines.store."
                path="/poster" />
            <div className="max-w-[820px] mx-auto mb-6 flex items-center justify-between print:hidden">
                <p className="text-sm text-zinc-600">Print-ready poster (Letter / A4). Scans of the QR codes are counted in Admin → Mobile.</p>
                <button onClick={() => window.print()} data-testid="poster-print-btn"
                    className="h-10 px-5 bg-[#4C1D95] text-white rounded-full text-sm flex items-center gap-2 hover:bg-[#2E1065]">
                    <Printer className="w-4 h-4" /> Print / Save as PDF
                </button>
            </div>

            <div className="max-w-[820px] mx-auto bg-white shadow-xl print:shadow-none overflow-hidden" data-testid="poster-sheet"
                style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                {/* Hero */}
                <div className="relative bg-[#2E1065] text-white px-12 pt-10 pb-12 overflow-hidden">
                    <img src={HERO_IMG} alt="" className="absolute right-0 top-0 h-full w-[55%] object-cover opacity-70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#2E1065] via-[#2E1065]/90 to-transparent" />
                    <div className="relative">
                        <img src="/flexline-wordmark-white.svg" alt="FlexLine Mobile" className="h-10 w-auto" />
                        <div className="mt-8 inline-block bg-[#D97706] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                            New · eSIM wireless service
                        </div>
                        <h1 className="mt-5 font-display text-6xl leading-[0.95] tracking-tight max-w-md">
                            Switch in minutes.<br /><span className="font-serif-italic text-[#6EE7B7]">Keep your number.</span>
                        </h1>
                        <p className="mt-5 text-lg text-[#E9D5FF] max-w-sm">Unlimited talk &amp; text with 5G/LTE data. No store, no SIM card, no contract.</p>
                        <div className="mt-6 flex items-end gap-2" data-testid="poster-from-price">
                            <span className="text-sm text-[#FCD34D] uppercase tracking-widest font-bold pb-2">From</span>
                            <span className="font-display text-7xl text-[#FCD34D] leading-none">$19.99</span>
                            <span className="text-[#E9D5FF] pb-2">/mo</span>
                        </div>
                    </div>
                </div>

                {/* Plans */}
                <div className="px-12 py-8">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-4">Pick your data — talk &amp; text are always unlimited</p>
                    <div className="grid grid-cols-4 gap-3">
                        {PLANS.map((p) => (
                            <div key={p.name} data-testid={`poster-plan-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`relative rounded-xl p-4 border ${p.popular ? "bg-[#4C1D95] text-white border-[#4C1D95]" : p.payg ? "bg-[#ECFDF5] border-[#A7F3D0]" : "bg-white border-[#DDD6FE]"}`}>
                                {p.popular && <span className="absolute -top-2.5 left-3 bg-[#D97706] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full">Most popular</span>}
                                <div className="text-sm font-semibold">{p.name}</div>
                                <div className="font-display text-3xl mt-1">${p.price}<span className={`text-xs ${p.popular ? "text-[#E9D5FF]" : "text-zinc-500"}`}>/mo</span></div>
                                <div className={`mt-2 text-xs flex gap-1 ${p.popular ? "text-[#FCD34D]" : "text-emerald-deep"}`}><Check className="w-3.5 h-3.5 shrink-0" />{p.data}</div>
                                <div className="mt-1 text-xs flex gap-1"><Check className={`w-3.5 h-3.5 shrink-0 ${p.popular ? "text-[#FCD34D]" : "text-emerald-deep"}`} />Unlimited talk &amp; text</div>
                                {p.note && <div className="mt-1 text-[10px] text-[#B45309]">{p.note}</div>}
                            </div>
                        ))}
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-3">+ $3.99/mo regulatory &amp; 911 fee and $15 one-time activation. Requires an unlocked eSIM-capable phone.</p>
                </div>

                {/* Steps */}
                <div className="px-12 pb-8 grid grid-cols-4 gap-3">
                    {STEPS.map(({ icon: Icon, t, d }, i) => (
                        <div key={t} className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#F3E8FF] grid place-items-center shrink-0"><Icon className="w-4 h-4 text-[#4C1D95]" /></div>
                            <div><div className="text-sm font-semibold">{i + 1}. {t}</div><div className="text-[11px] text-zinc-500">{d}</div></div>
                        </div>
                    ))}
                </div>

                {/* QR CTA */}
                <div className="bg-[#D97706] text-white px-12 py-8 flex items-center gap-8" data-testid="poster-qr-cta">
                    <div className="bg-white p-3 rounded-2xl shrink-0" data-testid="poster-qr-plans">
                        <QRCodeSVG value={PLANS_URL} size={170} level="M" fgColor="#2E1065" bgColor="#ffffff" />
                    </div>
                    <div className="flex-1">
                        <div className="font-display text-4xl leading-tight">Scan to pick your plan</div>
                        <div className="mt-1 text-white/90">Point your camera here — sign up and activate right from your phone.</div>
                        <div className="mt-3 font-mono text-lg font-bold">flexlines.store/wireless</div>
                        <div className="text-sm text-white/85">Questions? Call or text (877) 738-0943</div>
                    </div>
                    <div className="text-center shrink-0" data-testid="poster-qr-home">
                        <div className="bg-white p-2 rounded-xl inline-block"><QRCodeSVG value={HOME_URL} size={80} level="M" fgColor="#2E1065" bgColor="#ffffff" /></div>
                        <div className="text-[10px] mt-1 text-white/90 uppercase tracking-wider">All products</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
